import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import type { ReactNode, JSX } from 'react';
import { useAuth, getSupabaseClient } from './auth';
import { apiFetch } from './api-client';
import type { Region } from '@click2gro/shared';

export interface UserProfile {
  id: string;
  username?: string;
  first_name?: string;
  last_name?: string;
  /**
   * The zone every task time is entered and shown in — the COMPANY's, from its
   * state (GET /company/timezone), NOT profiles.timezone. That column defaults to
   * Australia/Adelaide and was never set per user, so a WA farm (Brookrise) saw
   * and entered SA times while its reports ran in Perth time. See fetchProfile.
   */
  timezone: string;
  company_id?: string;
  suburb?: string;
  state?: string;
}

interface ProfileContextValue {
  readonly profile: UserProfile | null;
  readonly loading: boolean;
  readonly error: string | null;
  /**
   * The active company's IANA zone, from its state — null while loading or if it
   * can't be read. Task times use ONLY this (hooks/use-task-times.tsx): never the
   * browser zone and never the stored profiles.timezone.
   */
  readonly companyTimezone: string | null;
  /**
   * The active company's currency + sales tax (AU: AUD $, GST 10%; GB: GBP £,
   * VAT 20%), from GET /company/region. Null while loading or if it can't be
   * read — use hooks/use-region.ts, never read this directly.
   */
  readonly companyRegion: Region | null;
  /**
   * The company's Payroll switch (companies.payroll_enabled), from GET
   * /company/region. false = Timesheets only (UK companies by default). Null
   * while loading or if it can't be read — use hooks/use-payroll-enabled.ts.
   */
  readonly companyPayrollEnabled: boolean | null;
  readonly refresh: () => void;
}

const ProfileContext = createContext<ProfileContextValue | null>(null);

/** The active company's IANA zone (from its state), or null if it can't be read. */
async function fetchCompanyTimezone(): Promise<string | null> {
  try {
    const res = await apiFetch('/api/v1/company/timezone');
    if (!res.ok) return null;
    const json = (await res.json()) as { timezone?: string };
    return json.timezone ?? null;
  } catch {
    return null;
  }
}

interface CompanyRegionResult {
  readonly region: Region | null;
  readonly payrollEnabled: boolean | null;
}

/** The active company's currency / tax region and Payroll switch (null = couldn't be read). */
async function fetchCompanyRegion(): Promise<CompanyRegionResult> {
  try {
    const res = await apiFetch('/api/v1/company/region');
    if (!res.ok) return { region: null, payrollEnabled: null };
    const json = (await res.json()) as { region?: Region; payrollEnabled?: unknown };
    const r = json.region;
    // The company's own currency + tax from the database (lib/countries.ts in the API).
    return {
      region: r && typeof r.currency === 'string' && typeof r.locale === 'string' && typeof r.taxRate === 'number' ? r : null,
      payrollEnabled: typeof json.payrollEnabled === 'boolean' ? json.payrollEnabled : null,
    };
  } catch {
    return { region: null, payrollEnabled: null };
  }
}

// ProfileProvider — fetches the Supabase profile ONCE per session and stores
// it in React Context. All useProfile() calls read from here: zero re-fetching
// on page navigation. It DOES re-fetch when the acting company changes: a
// superadmin picks a company at /select-company after sign-in, and the
// company's timezone / currency / Payroll switch must follow that choice
// (otherwise a UK company showed '$' — the region read before any company was
// selected 428s and falls back to AUD).
export function ProfileProvider({ children }: { readonly children: ReactNode }): JSX.Element {
  const { user, companyId } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [companyTimezone, setCompanyTimezone] = useState<string | null>(null);
  const [companyRegion, setCompanyRegion] = useState<Region | null>(null);
  const [companyPayrollEnabled, setCompanyPayrollEnabled] = useState<boolean | null>(null);

  const fetchProfile = useCallback(async () => {
    if (!user) {
      setProfile(null);
      setCompanyTimezone(null);
      setCompanyRegion(null);
      setCompanyPayrollEnabled(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const supabase = await getSupabaseClient();
      const [{ data, error: pgError }, companyTimezone, { region, payrollEnabled }] = await Promise.all([
        supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .maybeSingle(),
        fetchCompanyTimezone(),
        fetchCompanyRegion(),
      ]);

      if (pgError) throw pgError;
      const row = data as UserProfile | null;
      // The company's zone replaces the (always-Adelaide) profile column, so task
      // screens agree with the reports. If the lookup fails the stored value stays.
      setProfile(row && companyTimezone ? { ...row, timezone: companyTimezone } : row);
      setCompanyTimezone(companyTimezone);
      if (!region) console.warn('[Profile] Company region could not be read; currency shows as AUD');
      setCompanyRegion(region);
      setCompanyPayrollEnabled(payrollEnabled);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load profile';
      console.error('[Profile] Failed to fetch:', msg);
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // companyId: re-read the company's tz / currency / Payroll when it changes.
  useEffect(() => {
    void fetchProfile();
  }, [fetchProfile, companyId]);

  return (
    <ProfileContext.Provider value={{ profile, loading, error, companyTimezone, companyRegion, companyPayrollEnabled, refresh: fetchProfile }}>
      {children}
    </ProfileContext.Provider>
  );
}

export function useProfile(): ProfileContextValue {
  const ctx = useContext(ProfileContext);
  if (!ctx) throw new Error('useProfile must be used inside <ProfileProvider>');
  return ctx;
}

